import { Button, Input, useTranslation } from '@hbs-us/ui';
import type { HistoryState } from '@tanstack/react-router';
// HistoryState is re-exported (not redeclared) by the router; the interface itself is
// closed in this version and cannot be augmented from this package, so the candidates
// hand-off below casts through it. `MergeRoute` re-validates the state defensively.
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useRef, useState } from 'react';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { SkeletonRows } from '../_ops/SkeletonRows.js';
import { ClientsEmptyState } from './components/ClientsEmptyState.js';
import { ClientsTable } from './components/ClientsTable.js';
import type { Customer, ImportCandidate } from './contractsMirror.js';
import type { ClientsSearch } from './searchParams.js';
import { useImportClientsMutation } from './useClientsQuery.js';

/**
 * The clients list (the Clients app's first page). Header (title, subtitle, the
 * "New client" CTA + the "Import from documents" scan), a server-side search box
 * (`?q` — committed on submit so each keystroke does not fire a request), and the
 * clients table — or the empty state.
 *
 * The import result is a banner, not a route change: "Created N clients, linked M
 * documents", plus a "Review possible duplicates (K)" link to `/clients/merge`
 * when the scan found suspected duplicates. The candidate groups ride the router
 * STATE (not the URL — they are an ephemeral hand-off, and a bookmarkable merge
 * URL would go stale the moment the duplicates were merged).
 *
 * Loading → `_ops/SkeletonRows` shimmer; every color is a locked token (BRAND-06).
 */

export interface ClientsViewProps {
  /** The rows for the CURRENT `?q` (the route component owns the query). */
  clients: Customer[];
  /** The server's total for the current search (may exceed the page). */
  total: number;
  search: ClientsSearch;
  /** First-load shimmer (a background refetch keeps the rows). */
  loading?: boolean;
}

export function ClientsView({ clients, total, search, loading = false }: ClientsViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const importMutation = useImportClientsMutation();

  // Controlled search input; the URL `?q` is the source of truth (deep-linkable).
  const [queryText, setQueryText] = useState(search.q ?? '');
  // The import writes clients and links documents, so it asks first. It used to run on the
  // first click, with no word about what it would change.
  const [confirmingImport, setConfirmingImport] = useState(false);
  const headerImportRef = useRef<HTMLButtonElement>(null);
  const [importResult, setImportResult] = useState<{
    created: number;
    linked: number;
    candidates: ImportCandidate[];
  } | null>(null);

  const searching = Boolean(search.q);
  const hasData = clients.length > 0;
  const duplicateCount = importResult?.candidates.length ?? 0;

  function submitSearch(event: React.FormEvent) {
    event.preventDefault();
    const q = queryText.trim();
    void navigate({ to: '/clients', search: q ? { q } : {} });
  }

  // The dialog stays open while the import runs and closes when it settles, so it hands focus
  // back to an import button that is enabled again. Closing first left focus on <body>: both
  // triggers were disabled by the pending mutation at the moment the dialog tried to restore it.
  // Focus always lands on the HEADER's import button afterwards. The empty-state button can
  // unmount a moment later, when the list refetch brings the first clients in, and focus would
  // fall to <body> with it. The header button is on the page in every state.
  function closeImportDialog() {
    setConfirmingImport(false);
    requestAnimationFrame(() => headerImportRef.current?.focus());
  }

  function runImport() {
    importMutation.mutate(undefined, {
      onSuccess: (result) => {
        setImportResult(result);
        closeImportDialog();
      },
      onError: () => closeImportDialog(),
    });
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('clients:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('clients:subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            ref={headerImportRef}
            type="button"
            variant="secondary"
            onClick={() => setConfirmingImport(true)}
            disabled={importMutation.isPending}
          >
            {importMutation.isPending
              ? t('clients:actions.importing')
              : t('clients:actions.import')}
          </Button>
          <Button asChild>
            <Link to="/clients/new">{t('clients:actions.newClient')}</Link>
          </Button>
        </div>
      </header>

      <ConfirmDialog
        open={confirmingImport}
        heading={t('clients:import.confirmHeading')}
        body={t('clients:import.confirmBody')}
        confirmLabel={t('clients:import.confirm')}
        cancelLabel={t('clients:import.cancel')}
        onConfirm={runImport}
        // The import cannot be stopped once sent, and both triggers are disabled while it runs,
        // so Cancel and Escape wait for it to settle rather than dropping focus on <body>.
        onCancel={() => {
          if (!importMutation.isPending) setConfirmingImport(false);
        }}
        confirmPending={importMutation.isPending}
      />

      {importMutation.isError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm"
        >
          {t('clients:import.error')}
        </div>
      ) : null}

      {importResult ? (
        <div
          role="status"
          aria-live="polite"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card px-4 py-3"
        >
          <p className="text-foreground text-sm">
            {t('clients:import.result', {
              created: importResult.created,
              linked: importResult.linked,
            })}
          </p>
          {duplicateCount > 0 ? (
            <Link
              to="/clients/merge"
              state={{ candidates: importResult.candidates } as unknown as HistoryState}
              // `text-ring`, not `text-primary`. --primary is the vivid signal orange,
              // which measures 3.12:1 on the light card — below the 4.5:1 AA floor for
              // 14px text. --ring is the darker orange step in light and the vivid one
              // on the dark near-black, so this single utility clears AA in BOTH themes
              // (5.69:1 / 5.72:1) and ties the accent-text colour to the focus colour it
              // already shares. (Values named, not written: the BRAND-06 gate rejects a
              // hex literal in src/features/** even inside a comment.)
              className="font-semibold text-ring text-sm underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {t('clients:import.reviewDuplicates', { count: duplicateCount })}
            </Link>
          ) : null}
        </div>
      ) : null}

      {/* The count is the RESULT of the search, so from `sm` up it sits on the search's
          own row rather than floating alone between the box and the table with nothing
          to align to. Below `sm` the search takes the whole row and the count wraps
          under it — sharing a 375px line clipped the placeholder to
          "Search by name, email, or phc". */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <search className="w-full min-w-0 sm:w-auto sm:flex-1">
          <form onSubmit={submitSearch}>
            <label htmlFor="client-search" className="sr-only">
              {t('clients:search.label')}
            </label>
            <div className="relative max-w-md">
              <Search
                className="pointer-events-none absolute top-3.5 left-3 size-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="client-search"
                type="search"
                value={queryText}
                onChange={(event) => setQueryText(event.target.value)}
                placeholder={t('clients:search.placeholder')}
                className="pl-9"
              />
            </div>
          </form>
        </search>

        {/* Mounted in EVERY state so the live region exists before the number lands —
            a region inserted together with its own text is frequently not announced.
            It stays empty while loading: printing "0 clients" over six skeleton rows
            is the screen stating a total it does not have yet. */}
        <p aria-live="polite" className="shrink-0 text-muted-foreground text-sm">
          {loading ? '' : t('clients:table.count', { count: hasData ? total : 0 })}
        </p>
      </div>

      {loading ? (
        <section className="rounded-md border border-border bg-card p-4">
          <SkeletonRows rows={6} columns={5} label={t('clients:title')} />
        </section>
      ) : hasData ? (
        <ClientsTable clients={clients} />
      ) : (
        <ClientsEmptyState
          searched={searching}
          onImport={() => setConfirmingImport(true)}
          importing={importMutation.isPending}
        />
      )}
    </div>
  );
}
